import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from './TeacherOnboarding.module.scss';
import classes from 'classnames';
import { useAppDispatch, useAppSelector } from '@/store';
import { authActions, authSelector } from '@/store/reducers/auth';
import { useEffect, useState } from 'react';
import commonUtils from '@/utils/common';
import SVG from 'react-inlinesvg';
import { registerService } from '@/services/register';
import { CreateGroupDetails } from '@/types/register';
import UseScreenSize from '@/hooks/UseScreenSize';
import { TeacherGroup } from '@/types/user';

import OnboardingInvitation from '../OnboardingInvitation/OnboardingInvitation';
import TheButton from '../TheButton/TheButton';
import AppInput from '@/components/AppInput/AppInput';
import AppLoaderCircle from '../AppLoaderCircle';

import upArrow from '@/assets/svg/up-arrow.svg';
import downArrow from '@/assets/svg/down-arrow.svg';
import { useDrawer } from '@/context/DrawerContext';

interface TeacherOnboardingProps {
  className?: string;
  showTop?: boolean;
  showCopyBtn?: boolean;
  hasMultipleControls?: boolean;
  /** When true with hasMultipleControls, shows "Save and add students later" (create flow only). */
  showSaveClassLaterOption?: boolean;
  isDrawer?: boolean;
  classToEdit?: TeacherGroup | null;
  shouldNavigate?: boolean;
  emitCancel?: () => void;
}

const TeacherOnboarding = ({
  className,
  showTop = true,
  showCopyBtn = true,
  hasMultipleControls,
  showSaveClassLaterOption = false,
  isDrawer = false,
  classToEdit,
  shouldNavigate,
  emitCancel,
}: TeacherOnboardingProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(authSelector);
  const { isMobile } = UseScreenSize();

  const [link, setLink] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [showInvitation, setShowInvitation] = useState(false);

  const [teacherClassName, setTeacherClassName] = useState('');

  const [seletedGrade, setSelectedGrade] = useState('');
  const [selectedGradeId, setSelectedGradeId] = useState('');
  const [grades, setGrades] = useState<{ name: string; id: string }[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const drawer = useDrawer();

  useEffect(() => {
    setGrades(
      [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map(id => {
        return {
          id: id.toString(),
          name: t(`grade_${id}`),
        };
      })
    );
  }, []);

  useEffect(() => {
    if (classToEdit && classToEdit.invitationLinkForStudents) {
      setLink(classToEdit.invitationLinkForStudents);
      setTeacherClassName(classToEdit.name);
      const gradeId = classToEdit.grade;
      setSelectedGradeId(gradeId);
      setSelectedGrade(commonUtils.getGradeText(gradeId, t));
      setShowInvitation(true);
    }
  }, [classToEdit]);

  useEffect(() => {
    setTeacherClassName('');
    setSelectedGrade('');
    setSelectedGradeId('');
    setLink('');
  }, [drawer.isAddClassDrawerOpen]);

  useEffect(() => {
    window.addEventListener('click', () => {
      if (!showDropdown) {
        return;
      }

      onCloseDropdown();
    });

    return () => {
      window.removeEventListener('click', () => {});
    };
  }, [showDropdown]);

  const onToggleDropdown = (e: React.MouseEvent<HTMLElement>) => {
    if (showInvitation && !classToEdit) {
      return;
    }

    e.stopPropagation();
    setShowDropdown(prevState => !prevState);
  };

  const onCloseDropdown = () => {
    setShowDropdown(false);
  };

  const onSelectedOption = (
    e: React.MouseEvent<HTMLElement>,
    grade: { name: string; id: string }
  ) => {
    e.stopPropagation();
    onCloseDropdown();
    setSelectedGrade(grade.name);
    setSelectedGradeId(grade.id);
  };

  const onClose = () => {
    if (!classToEdit) {
      setTeacherClassName('');
      setSelectedGrade('');
      setLink('');
      setShowInvitation(false);
    }

    emitCancel?.();
  };

  const onEditClass = async () => {
    if (!classToEdit) {
      return;
    }
  };

  const gradeOptions = grades.map(grade => {
    return (
      <div
        key={grade.id}
        className={classNames.grade}
        onClick={event => onSelectedOption(event, grade)}
      >
        {grade.name}
      </div>
    );
  });

  const gradesDropdown = showDropdown && (
    <div className={classNames.gradesDropdown}>{gradeOptions}</div>
  );

  const gradeText = seletedGrade;

  let shouldDisableBtn = false;

  if (!teacherClassName || !seletedGrade) {
    shouldDisableBtn = true;
  } else if (classToEdit) {
    shouldDisableBtn =
      classToEdit.name.trim() === teacherClassName.trim() && classToEdit.grade === selectedGradeId;
  }

  const createClass = async (mode: 'saveLater' | 'inviteNow') => {
    if (showInvitation) {
      return onClose();
    }
    if (shouldDisableBtn) {
      return;
    }
    setIsLoading(true);

    try {
      const groupDetails: CreateGroupDetails = {
        groupName: teacherClassName.trim(),
        grade: selectedGradeId.trim(),
      };
      const response = (await registerService.createGroup(user, groupDetails))?.data.data;

      if (response) {
        setLink('');

        dispatch(
          authActions.addNewClass({
            id: response.groupId,
            name: groupDetails.groupName,
            grade: groupDetails.grade,
            invitationLinkForStudents: '',
            groupLevel: '',
            ...(response.classCode && { classCode: response.classCode }),
          })
        );

        if (mode === 'saveLater') {
          onClose();
        } else {
          setShowInvitation(true);
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  const gradesSelect = (
    <div className={classNames.gradesSelectContainer}>
      <span className={classNames.label}>{`*${t('grade')}`}</span>
      <div
        className={classes(classNames.gradesSelect, {
          'cursor-pointer': !showInvitation || (showInvitation && classToEdit),
        })}
        onClick={onToggleDropdown}
      >
        <span className={!seletedGrade ? 'opacity-20' : ''}>
          {seletedGrade ? gradeText : t('chooseGrade')}
        </span>
        {(!showInvitation || (showInvitation && classToEdit)) && (
          <SVG src={showDropdown ? upArrow : downArrow} />
        )}
      </div>
      {gradesDropdown}
    </div>
  );

  const confirmBtn = (
    <TheButton
      className={hasMultipleControls ? classNames.saveBtn : classNames.invitationBtn}
      text={hasMultipleControls && showInvitation ? t('done') : t('inviteYourStudents')}
      disabled={hasMultipleControls && showInvitation ? false : shouldDisableBtn}
      shouldRecolor={false}
      emitOnClick={() => (classToEdit ? onEditClass() : createClass('inviteNow'))}
    />
  );

  const content = showInvitation ? (
    <OnboardingInvitation
      className={classNames.onboardingInvitation}
      link={link}
      showCopyBtn={showCopyBtn}
      shouldNavigate={shouldNavigate}
    />
  ) : (
    <>{!hasMultipleControls && confirmBtn}</>
  );

  if (isLoading) {
    return <AppLoaderCircle className={classNames.loader} />;
  }

  return (
    <div
      className={classes(classNames.teacherOnboarding, className, {
        [classNames.teacherOnboardingDrawer]: isDrawer,
      })}
    >
      {showTop && (
        <div className={classNames.top}>
          <span>{`${t('welcome')}, ${user?.metadata?.firstName}!`}</span>
          <h2>
            {isMobile
              ? t('createYourFirstClass')
              : `${t('createYourFirstClass')} ${t('andThenInvite')}`}
          </h2>
          <span className={classNames.tip}>{t('youCanCreateMore')}</span>
        </div>
      )}
      <div className={classNames.body}>
        <div className={classNames.inputs}>
          <AppInput
            className={classNames.input}
            inputWrapperClassName={classNames.inputWrapperClassName}
            name='className'
            label={t('className') ?? ''}
            placeholder={(isMobile ? t('writeHere') : t('writeClassNameHere')) ?? ''}
            value={teacherClassName}
            isRequired
            emitChange={({ target }) => setTeacherClassName(target.value)}
          />
          {gradesSelect}
        </div>
        {content}
        {hasMultipleControls && (
          <div className={classNames.controls}>
            <TheButton plain text={t('cancel')} emitOnClick={onClose} />
            <div className={classNames.controlsPrimary}>
              {!showInvitation && showSaveClassLaterOption && (
                <TheButton
                  className={classNames.saveClassLaterBtn}
                  text={t('admin.schoolDetailsPage.classes.saveClassAndAddLater')}
                  disabled={shouldDisableBtn}
                  shouldRecolor={false}
                  emitOnClick={() => createClass('saveLater')}
                />
              )}
              {confirmBtn}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherOnboarding;
